import React from "react";
import styles from "./Register.module.scss";
import {useNavigate, useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Formik, Form, Field, ErrorMessage} from "formik";
import {useRegisterValidationSchema} from "./validation";
import {useActions} from "../../hook/useActions";

// Registration success page component
const RegistrationSuccess: React.FC<{ onLogin: () => void }> = ({ onLogin }) => {
    const { t } = useTranslation();
    return (
        <div className={styles.registration} style={{ justifyContent: "center", alignItems: "center", width: "50%" }}>
            <div className={styles.container} style={{ alignItems: "center", justifyContent: "center" }}>
                <div className={styles.title} style={{textAlign: "center", width: "100%"}}>{t("Register.SuccessTitle", "Registration Successful!")}</div>
                <div style={{ margin: "30px 0", textAlign: "center"}}>
                    {t("Register.SuccessMessage", "Your account has been created. We have sent a confirmation link to your email.")}
                </div>
                <button
                    className={`${styles.button} ${styles.loginButton}`}
                    style={{ position: "static", marginTop:"-20px", width: 180 }}
                    onClick={onLogin}
                >
                    <span className={styles.buttonText}>{t("Register.Login")}</span>
                </button>
            </div>
        </div>
    );
};

const Register: React.FC = () => {
    const [userRegistered, setUserRegistered] = React.useState(false);
    const [acceptTOS, setAcceptTOS] = React.useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const {t} = useTranslation();
    const validationSchema = useRegisterValidationSchema();
    const {RegisterUser} = useActions();

    if(userRegistered) {
        return <RegistrationSuccess onLogin={() => navigate('/login')} />;
    }

    // Autofill referrerNumber from URL if valid
    const getReferrerFromUrl = () => {
        const params = new URLSearchParams(location.search);
        const ref = params.get("referrerNumber");
        if (ref && /^\d{10}$/.test(ref)) {
            return ref;
        }
        return "2500000001";
    };

    return (
        <div className={styles.registration}>
            <div className={styles.container}>
                <Formik
                    initialValues={{
                        referrerNumber: getReferrerFromUrl(),
                        fullName: "",
                        mobile: "",
                        email: "",
                        country: "",
                        city: "",
                        password: "",
                        confirmPassword: ""
                    }}
                    validationSchema={validationSchema}
                    onSubmit={async (values) => {
                        const user = {
                            fullName: values.fullName,
                            recomendatorNumber: values.referrerNumber,
                            phoneNumber: values.mobile,
                            country: values.country,
                            city: values.city,
                            email: values.email,
                            password: values.password,
                        };
                        try {
                            await RegisterUser(user);
                            navigate('/login');
                            // setUserRegistered(true);
                        } catch (error) {
                            console.error("Registration error:", error);
                        }
                    }}
                >
                    {({handleChange, handleSubmit, values, isValid, dirty, status, isSubmitting}) => (
                        <Form className={styles.form} onSubmit={handleSubmit}>
                            <div className={styles.header}>
                                <div className={styles.title}>{t("Register.Title")}</div>
                            </div>
                            <div className={styles.inputGroup}>
                                <div className={styles.label}>{t("Register.ReferrerNumber")}</div>
                                <Field
                                    name="referrerNumber"
                                    className={styles.input}
                                    style={{height: 44}}
                                    value={values.referrerNumber}
                                    onChange={handleChange}
                                />
                                <ErrorMessage name="referrerNumber" component="div" className={styles.error}/>
                            </div>
                            <div className={styles.inputGroup}>
                                <div className={styles.label}>{t("Register.FullName")}</div>
                                <Field
                                    name="fullName"
                                    className={styles.inputAlt}
                                    style={{height: 44}}
                                    value={values.fullName}
                                    onChange={handleChange}
                                />
                                <ErrorMessage name="fullName" component="div" className={styles.error}/>
                            </div>
                            <div className={styles.inputGroup}>
                                <div className={styles.label}>{t("Register.Mobile")}</div>
                                <Field
                                    name="mobile"
                                    className={styles.input}
                                    style={{height: 38}}
                                    value={values.mobile}
                                    onChange={handleChange}
                                />
                                <ErrorMessage name="mobile" component="div" className={styles.error}/>
                            </div>
                            <div className={styles.inputGroup}>
                                <div className={styles.label}>{t("Register.Email")}</div>
                                <Field
                                    name="email"
                                    type="email"
                                    className={styles.inputAlt}
                                    style={{height: 38}}
                                    value={values.email}
                                    onChange={handleChange}
                                />
                                <ErrorMessage name="email" component="div" className={styles.error}/>
                            </div>
                            <div className={styles.inputGroup}>
                                <div className={styles.label}>{t("Register.Country")}</div>
                                <Field
                                    name="country"
                                    className={styles.input}
                                    style={{height: 38}}
                                    value={values.country}
                                    onChange={handleChange}
                                />
                                <ErrorMessage name="country" component="div" className={styles.error}/>
                            </div>
                            <div className={styles.inputGroup}>
                                <div className={styles.label}>{t("Register.City")}</div>
                                <Field
                                    name="city"
                                    className={styles.input}
                                    style={{height: 38}}
                                    value={values.city}
                                    onChange={handleChange}
                                />
                                <ErrorMessage name="city" component="div" className={styles.error}/>
                            </div>
                            <div className={styles.inputGroup}>
                                <div className={styles.label}>{t("Register.Password")}</div>
                                <Field
                                    name="password"
                                    type="password"
                                    className={styles.inputAlt2}
                                    style={{height: 38}}
                                    value={values.password}
                                    onChange={handleChange}
                                />
                                <ErrorMessage name="password" component="div" className={styles.error}/>
                            </div>
                            <div className={styles.inputGroup}>
                                <div className={styles.label}>{t("Register.ConfirmPassword")}</div>
                                <Field
                                    name="confirmPassword"
                                    type="password"
                                    className={styles.inputAlt2}
                                    style={{height: 38}}
                                    value={values.confirmPassword}
                                    onChange={handleChange}
                                />
                                <ErrorMessage name="confirmPassword" component="div" className={styles.error}/>
                            </div>
                            {/* Accept TOS checkbox */}
                            <div style={{ marginTop: 20, marginBottom: 10 }}>
                                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <input
                                        type="checkbox"
                                        checked={acceptTOS}
                                        onChange={e => setAcceptTOS(e.target.checked)}
                                        style={{ width: 18, height: 18 }}
                                    />
                                    <span style={{ whiteSpace: "nowrap" }}>
                                        {t("Register.IAccept", "I accept the Terms of Service")}
                                    </span>
                                    <a href="/PrivacyPolicy.pdf" target={"_blank"} rel="noopener noreferrer" style={{ whiteSpace: "nowrap" }}>
                                        {t("Register.PrivacyPolicy", "Privacy Policy")}
                                    </a>
                                </label>
                            </div>
                            <div className={styles.buttonsContainer}>
                                <button
                                    type="button"
                                    className={`${styles.button} ${styles.loginButton}`}
                                    onClick={() => navigate('/login')}
                                >
                                    <span className={styles.buttonText}>{t("Register.Login")}</span>
                                </button>
                                <button
                                    type="submit"
                                    className={`${styles.button} ${styles.registerButton} ${!(isValid && dirty && acceptTOS) ? styles.disabledButton : ''}`}
                                    disabled={!(isValid && dirty && acceptTOS) || isSubmitting}
                                >
                                    <span className={styles.buttonText}>{t("Register.Register")}</span>
                                </button>
                            </div>
                            {status && status.message && (
                                <div className={status.success ? styles.success : styles.error}>
                                    {status.message}
                                </div>
                            )}
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
}

export default Register;
